/**
 * House Settings Page
 * Matrix table: houses as rows, upcoming courses as columns.
 * Inline-editable capacity and gender designation per cell.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHouseSettings } from '../../hooks/useHouseSettings';
import { useToast } from '../../hooks/use-toast';
import { Button } from '../ui/button';
import { CourseTimeToggle } from '../ui/course-time-toggle';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { DialogDescription } from '../ui/dialog';
import { Label } from '../ui/label';
import { Plus, Pencil, Trash2, Save, Loader2 } from 'lucide-react';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emptyCell() {
  return { capacity: 0, gender_designation: 'M', capacity_uuid: null };
}

function buildLocalMatrix(houses, courses, matrix) {
  const local = {};
  for (const house of houses) {
    local[house.uuid] = {};
    for (const course of courses) {
      const cell = matrix[house.uuid]?.[course.uuid];
      local[house.uuid][course.uuid] = cell
        ? { capacity: cell.capacity, gender_designation: cell.gender_designation, capacity_uuid: cell.capacity_uuid }
        : emptyCell();
    }
  }
  return local;
}

// ─── Cell editor ──────────────────────────────────────────────────────────────

const GENDER_CELL_BG  = { M: 'bg-blue-50 dark:bg-blue-900/30',  F: 'bg-rose-50 dark:bg-rose-900/30'  };
const GENDER_INPUT_BG = { M: 'bg-blue-50 dark:bg-blue-900/30',  F: 'bg-rose-50 dark:bg-rose-900/30'  };
const GENDER_SELECT_BG = { M: 'bg-blue-100 border-blue-200 text-blue-800 dark:bg-blue-900/40 dark:border-blue-700 dark:text-blue-300', F: 'bg-rose-100 border-rose-200 text-rose-800 dark:bg-rose-900/40 dark:border-rose-700 dark:text-rose-300' };

function CapacityCell({ value, onChange, disabled, changed, maxCapacity }) {
  const { t } = useTranslation();
  const gender = value.gender_designation || 'M';
  const cellBg = GENDER_CELL_BG[gender] ?? '';
  const inputBg = GENDER_INPUT_BG[gender] ?? '';
  const selectBg = GENDER_SELECT_BG[gender] ?? '';
  const handleCapacityChange = (e) => {
    const raw = parseInt(e.target.value, 10) || 0;
    const clamped = maxCapacity != null ? Math.min(raw, maxCapacity) : raw;
    onChange({ ...value, capacity: clamped });
  };
  return (
    <div className={`flex items-center gap-1 rounded-md px-1.5 py-1 ${cellBg} ${changed ? 'ring-2 ring-amber-400 ring-offset-1' : ''}`}>
      <Input
        type="number"
        min={0}
        max={maxCapacity ?? 99}
        value={value.capacity}
        onChange={handleCapacityChange}
        className={`w-12 h-8 text-center text-sm px-1 border-0 ${inputBg}`}
        disabled={disabled}
      />
      <Select
        value={gender}
        onValueChange={(v) => onChange({ ...value, gender_designation: v })}
        disabled={disabled}
      >
        <SelectTrigger className={`h-8 w-20 text-xs ${selectBg}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="M">{t('houseSettings.male')}</SelectItem>
          <SelectItem value="F">{t('houseSettings.female')}</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

// ─── Add / Edit modal ─────────────────────────────────────────────────────────

const EMPTY_FORM = {
  name: '',
  max_capacity: '',
  address: '',
  eircode: '',
  contact_mobile: '',
  contact_landline: '',
  is_active: true,
  owner_name: '',
  owner_email: '',
};

function HouseModal({ open, onClose, onSave, initial, isSaving }) {
  const { t } = useTranslation();
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name ?? '',
              max_capacity: initial.max_capacity ?? '',
              address: initial.address ?? '',
              eircode: initial.eircode ?? '',
              contact_mobile: initial.contact_mobile ?? '',
              contact_landline: initial.contact_landline ?? '',
              is_active: initial.is_active ?? true,
              owner_name: initial.owner_name ?? '',
              owner_email: initial.owner_email ?? '',
            }
          : EMPTY_FORM
      );
    }
  }, [open, initial]);

  const set = (field) => (e) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    const capacity = parseInt(form.max_capacity, 10) || 0;
    onSave({
      name: form.name.trim(),
      max_capacity: capacity,
      address: form.address.trim(),
      eircode: form.eircode.trim(),
      contact_mobile: form.contact_mobile.trim(),
      contact_landline: form.contact_landline.trim(),
      is_active: capacity > 0,
      owner_name: form.owner_name.trim(),
      owner_email: form.owner_email.trim(),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {initial ? t('houseSettings.editHouse') : t('houseSettings.addHouse')}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 space-y-1">
              <Label>{t('houseSettings.name')} *</Label>
              <Input value={form.name} onChange={set('name')} required />
            </div>
            <div className="space-y-1">
              <Label>{t('houseSettings.maxCapacity')} *</Label>
              <Input
                type="number"
                min={0}
                max={50}
                value={form.max_capacity}
                onChange={set('max_capacity')}
                required
              />
            </div>
            <div className="space-y-1">
              <Label>{t('houseSettings.mobile')}</Label>
              <Input value={form.contact_mobile} onChange={set('contact_mobile')} />
            </div>
            <div className="col-span-2 space-y-1">
              <Label>{t('houseSettings.address')}</Label>
              <Input value={form.address} onChange={set('address')} />
            </div>
            <div className="space-y-1">
              <Label>{t('houseSettings.eircode')}</Label>
              <Input value={form.eircode} onChange={set('eircode')} />
            </div>
            <div className="space-y-1">
              <Label>{t('houseSettings.landline')}</Label>
              <Input value={form.contact_landline} onChange={set('contact_landline')} />
            </div>
          </div>

          <div className="border-t pt-3 space-y-3">
            <p className="text-sm font-medium text-muted-foreground">
              {t('houseSettings.houseOwner')}
            </p>
            <div className="space-y-1">
              <Label>{t('houseSettings.ownerName')}</Label>
              <Input value={form.owner_name} onChange={set('owner_name')} />
            </div>
            <div className="space-y-1">
              <Label>{t('houseSettings.ownerEmail')} *</Label>
              <Input
                type="email"
                value={form.owner_email}
                onChange={set('owner_email')}
                placeholder="owner@example.com"
                required
              />
              <p className="text-xs text-muted-foreground">
                {t('houseSettings.ownerEmailHint')}
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function HouseSettings() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const {
    houses,
    courses,
    matrix,
    enrollment,
    isLoading,
    error,
    saveMatrix,
    isSavingMatrix,
    createHouse,
    isCreating,
    updateHouse,
    isUpdating,
    deleteHouse,
    isDeleting,
  } = useHouseSettings();

  // Local editable matrix state
  const [localMatrix, setLocalMatrix] = useState({});
  const [isDirty, setIsDirty] = useState(false);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);

  // Delete confirmation state
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Course capacity update prompt state
  const [capacityUpdatePrompt, setCapacityUpdatePrompt] = useState(null); // { houseName, houseUuid, oldCapacity, newCapacity }

  // Sync server data → local state (only when not dirty)
  useEffect(() => {
    if (!isDirty && houses.length > 0) {
      setLocalMatrix(buildLocalMatrix(houses, courses, matrix));
    }
  }, [houses, courses, matrix, isDirty]);

  const updateCell = (houseUuid, courseUuid, value) => {
    setLocalMatrix((prev) => ({
      ...prev,
      [houseUuid]: { ...prev[houseUuid], [courseUuid]: value },
    }));
    setIsDirty(true);
  };

  const summaryStats = useMemo(() => {
    const activeHouses = houses.filter(h => h.is_active);
    const inactiveHouses = houses.filter(h => !h.is_active);
    const totalMaxCapacity = activeHouses.reduce((sum, h) => sum + h.max_capacity, 0);

    const byCourse = {};
    for (const course of courses) {
      let maleHouses = 0, femaleHouses = 0, maleCapacity = 0, femaleCapacity = 0;
      for (const house of activeHouses) {
        const cell = localMatrix[house.uuid]?.[course.uuid];
        if (!cell || cell.capacity === 0) continue;
        if (cell.gender_designation === 'M') {
          maleHouses++;
          maleCapacity += cell.capacity;
        } else {
          femaleHouses++;
          femaleCapacity += cell.capacity;
        }
      }
      const enrolled = enrollment[course.uuid] ?? { male: 0, female: 0 };
      byCourse[course.uuid] = {
        maleHouses, femaleHouses, maleCapacity, femaleCapacity,
        enrolledMale: enrolled.male,
        enrolledFemale: enrolled.female,
      };
    }

    return { activeHouses: activeHouses.length, inactiveHouses: inactiveHouses.length, totalMaxCapacity, byCourse };
  }, [houses, courses, localMatrix, enrollment]);

  const pendingChangeCount = useMemo(() => {
    let count = 0;
    for (const house of houses.filter(h => h.is_active)) {
      for (const course of courses) {
        const cell = localMatrix[house.uuid]?.[course.uuid];
        if (!cell) continue;
        const original = matrix[house.uuid]?.[course.uuid];
        if (cell.capacity_uuid) {
          if (cell.capacity !== original?.capacity || cell.gender_designation !== original?.gender_designation) count++;
        } else if (cell.capacity > 0) {
          count++;
        }
      }
    }
    return count;
  }, [localMatrix, matrix, houses, courses]);

  const handleUpdateAllCourseCapacities = async () => {
    const { houseUuid, newCapacity } = capacityUpdatePrompt;
    const updates = [];
    for (const course of courses) {
      const cell = localMatrix[houseUuid]?.[course.uuid];
      if (cell?.capacity_uuid) {
        updates.push({
          capacity_uuid: cell.capacity_uuid,
          capacity: newCapacity,
          gender_designation: cell.gender_designation,
        });
      }
    }
    setCapacityUpdatePrompt(null);
    if (updates.length === 0) return;
    try {
      await saveMatrix(updates);
      toast({
        title: t('houseSettings.saveSuccess'),
        description: t('houseSettings.houseUpdatedDesc', { name: capacityUpdatePrompt?.houseName }),
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('houseSettings.saveError'),
        description: getApiError(err),
        variant: 'destructive',
      });
    }
  };

  const handleDiscardChanges = () => {
    setLocalMatrix(buildLocalMatrix(houses, courses, matrix));
    setIsDirty(false);
  };

  const getApiError = (err) =>
    err?.response?.data?.detail ||
    Object.values(err?.response?.data || {}).flat().join(' ') ||
    err?.message ||
    t('common.error');

  const handleSaveMatrix = async () => {
    // Only send cells that actually changed vs the server state
    const capacities = [];
    for (const house of houses) {
      for (const course of courses) {
        const cell = localMatrix[house.uuid]?.[course.uuid];
        if (!cell) continue;
        const original = matrix[house.uuid]?.[course.uuid];
        if (cell.capacity_uuid) {
          // Existing record — only include if something changed
          if (
            cell.capacity !== original?.capacity ||
            cell.gender_designation !== original?.gender_designation
          ) {
            capacities.push({
              capacity_uuid: cell.capacity_uuid,
              capacity: cell.capacity,
              gender_designation: cell.gender_designation,
            });
          }
        } else if (cell.capacity > 0) {
          // New record — only create if capacity is non-zero
          capacities.push({
            house: house.uuid,
            course: course.uuid,
            capacity: cell.capacity,
            gender_designation: cell.gender_designation,
          });
        }
      }
    }

    try {
      await saveMatrix(capacities);
      setIsDirty(false);
      toast({
        title: t('houseSettings.saveSuccess'),
        description: `${capacities.length} ${capacities.length === 1 ? 'change' : 'changes'} saved`,
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('houseSettings.saveError'),
        description: getApiError(err),
        variant: 'destructive',
      });
    }
  };

  const handleSaveHouse = async (data) => {
    const isEditing = !!editingHouse;
    const wasActive = editingHouse?.is_active;
    const nowActive = data.is_active;
    const oldCapacity = editingHouse?.max_capacity;
    const newCapacity = data.max_capacity;
    try {
      if (isEditing) {
        await updateHouse({ uuid: editingHouse.uuid, ...data });
      } else {
        await createHouse(data);
      }
      setModalOpen(false);
      // If active status changed, clear that house's cells from localMatrix
      // so it rebuilds correctly from fresh server data
      if (isEditing && wasActive !== nowActive) {
        setLocalMatrix((prev) => {
          const next = { ...prev };
          delete next[editingHouse.uuid];
          return next;
        });
      }
      setEditingHouse(null);

      // Prompt to update course capacities if max changed on an active house
      if (isEditing && nowActive && oldCapacity !== newCapacity) {
        setCapacityUpdatePrompt({
          houseName: data.name,
          houseUuid: editingHouse.uuid,
          oldCapacity,
          newCapacity,
        });
      }

      if (!isEditing) {
        toast({
          title: t('houseSettings.houseCreated'),
          description: t('houseSettings.houseCreatedDesc', { name: data.name }),
          variant: 'success',
        });
      } else if (wasActive !== nowActive) {
        toast({
          title: nowActive
            ? t('houseSettings.houseActivated', { name: data.name })
            : t('houseSettings.houseDeactivated', { name: data.name }),
          description: t('houseSettings.houseUpdatedDesc', { name: data.name }),
          variant: 'success',
        });
      } else {
        toast({
          title: t('houseSettings.houseUpdated'),
          description: t('houseSettings.houseUpdatedDesc', { name: data.name }),
          variant: 'success',
        });
      }
    } catch (err) {
      toast({
        title: t('houseSettings.saveError'),
        description: getApiError(err),
        variant: 'destructive',
      });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const name = deleteTarget.name;
    try {
      await deleteHouse(deleteTarget.uuid);
      setDeleteTarget(null);
      toast({
        title: t('houseSettings.deleteSuccess'),
        description: t('houseSettings.deleteSuccessDesc', { name }),
        variant: 'success',
      });
    } catch (err) {
      toast({
        title: t('houseSettings.deleteError'),
        description: getApiError(err),
        variant: 'destructive',
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-16 text-destructive">
        {t('common.errorLoading')}
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${isDirty ? 'pb-20' : ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t('houseSettings.title')}</h1>
          <p className="text-muted-foreground mt-1">{t('houseSettings.description')}</p>
        </div>
        <div className="flex items-center gap-2">
          <CourseTimeToggle />
          <Button
            variant="outline"
            onClick={() => { setEditingHouse(null); setModalOpen(true); }}
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('houseSettings.addHouse')}
          </Button>
        </div>
      </div>

      {/* Matrix table */}
      {courses.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          {t('houseSettings.noUpcomingCourses')}
        </div>
      ) : (
        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-muted/70 border-b-2 border-border">
                <th className="text-center px-2 py-3 font-semibold w-12 min-w-[48px] sticky left-0 bg-muted/70 z-10 border-r border-border">
                  #
                </th>
                <th className="text-left px-2 py-3 font-semibold w-[240px] min-w-[240px] max-w-[240px] sticky left-12 bg-muted/70 z-10 border-r border-border overflow-hidden">
                  {t('houseSettings.house')}
                </th>
                <th className="text-center px-4 py-3 font-semibold w-20 border-r border-border">
                  {t('houseSettings.maxCapacity')}
                </th>
                {courses.map((course) => (
                  <th key={course.uuid} className="text-center px-3 py-3 font-semibold min-w-[155px] border-r border-border last:border-r-0">
                    <div>{course.name}</div>
                    <div className="text-xs font-normal text-muted-foreground mt-0.5">
                      {t('houseSettings.capacity')} / {t('houseSettings.gender')}
                    </div>
                  </th>
                ))}
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody>
              {/* Summary row */}
              <tr className="border-b-2 border-border bg-muted/30 text-sm">
                <td className="px-2 py-3 sticky left-0 z-10 bg-muted/30 border-r border-border w-12 min-w-[48px]" />
                <td className="px-2 py-3 sticky left-12 z-10 bg-muted/30 border-r border-border w-[240px] min-w-[240px] max-w-[240px] overflow-hidden">
                  <div className="font-semibold text-foreground">{t('houseSettings.summary')}</div>
                  <div className="flex gap-1 mt-1">
                    <span className="inline-flex items-center text-xs text-blue-700 bg-blue-50 border border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                      ✓ {summaryStats.activeHouses}
                    </span>
                    {summaryStats.inactiveHouses > 0 && (
                      <span className="inline-flex items-center text-xs text-amber-700 bg-amber-50 border border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-700 rounded px-1.5 py-0.5 whitespace-nowrap">
                        ⊘ {summaryStats.inactiveHouses}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-center font-semibold border-r border-border">
                  <div className="text-foreground">{summaryStats.totalMaxCapacity}</div>
                  <div className="text-xs text-muted-foreground">{t('houseSettings.totalBeds')}</div>
                </td>
                {courses.map((course) => {
                  const s = summaryStats.byCourse[course.uuid] ?? { maleHouses: 0, femaleHouses: 0, maleCapacity: 0, femaleCapacity: 0, enrolledMale: 0, enrolledFemale: 0 };
                  const maleShort = s.enrolledMale > 0 && s.maleCapacity < s.enrolledMale;
                  const femaleShort = s.enrolledFemale > 0 && s.femaleCapacity < s.enrolledFemale;
                  return (
                    <td key={course.uuid} className="px-2 py-2 border-r border-border last:border-r-0">
                      <div className="flex flex-col gap-1 items-center">
                        <div className={`inline-flex items-center gap-1.5 text-xs border rounded px-2 py-0.5 w-full justify-between ${maleShort ? 'bg-red-50 text-red-800 border-red-300 dark:bg-red-900/40 dark:text-red-300 dark:border-red-700' : 'bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-700'}`}>
                          <span className="font-semibold">{t('houseSettings.maleAbbr')} · {s.maleHouses}h</span>
                          <span>{s.maleCapacity} / {s.enrolledMale}</span>
                        </div>
                        <div className={`inline-flex items-center gap-1.5 text-xs border rounded px-2 py-0.5 w-full justify-between ${femaleShort ? 'bg-red-50 text-rose-800 border-red-300 dark:bg-red-900/40 dark:text-rose-300 dark:border-red-700' : 'bg-rose-50 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-300 dark:border-rose-700'}`}>
                          <span className="font-semibold">{t('houseSettings.femaleAbbr')} · {s.femaleHouses}h</span>
                          <span>{s.femaleCapacity} / {s.enrolledFemale}</span>
                        </div>
                      </div>
                    </td>
                  );
                })}
                <td className="px-4 py-3" />
              </tr>

              {houses.length === 0 ? (
                <tr>
                  <td
                    colSpan={4 + courses.length}
                    className="text-center py-10 text-muted-foreground"
                  >
                    {t('houseSettings.noHouses')}
                  </td>
                </tr>
              ) : (
                houses.map((house, idx) => {
                  const isInactive = !house.is_active;
                  const rowBg = isInactive
                    ? 'bg-muted/10'
                    : idx % 2 === 0 ? 'bg-card' : 'bg-muted/20';
                  return (
                    <tr
                      key={house.uuid}
                      className={`group border-b border-border transition-colors hover:bg-muted/50 ${rowBg} ${isInactive ? 'opacity-60' : ''}`}
                    >
                      {/* House # — sticky */}
                      <td className={`px-2 py-3 sticky left-0 z-10 border-r border-border transition-colors group-hover:bg-muted/50 w-12 min-w-[48px] text-center text-sm font-mono text-muted-foreground ${rowBg}`}>
                        {house.id}
                      </td>
                      {/* House name + status — sticky */}
                      <td className={`px-2 py-3 sticky left-12 z-10 border-r border-border transition-colors group-hover:bg-muted/50 w-[240px] min-w-[240px] max-w-[240px] overflow-hidden ${rowBg}`}>
                        <div className="flex items-center gap-1 min-w-0">
                          <span className="font-medium truncate">{house.name}</span>
                          {isInactive && (
                            <Badge className="text-xs bg-amber-100 text-amber-700 border border-amber-300 hover:bg-amber-100 shrink-0">
                              {t('houseSettings.inactive')}
                            </Badge>
                          )}
                        </div>
                        {house.owner_name && (
                          <div className="text-xs text-muted-foreground mt-0.5 truncate">
                            {house.owner_name}
                          </div>
                        )}
                      </td>

                      {/* Max capacity (read-only display) */}
                      <td className="px-4 py-3 text-center font-semibold text-muted-foreground border-r border-border">
                        {house.max_capacity}
                      </td>

                      {/* Per-course capacity + gender cells */}
                      {courses.map((course) => {
                        const cell = localMatrix[house.uuid]?.[course.uuid] ?? emptyCell();
                        const original = matrix[house.uuid]?.[course.uuid];
                        const changed = original
                          ? cell.capacity !== original.capacity || cell.gender_designation !== original.gender_designation
                          : cell.capacity > 0;
                        return (
                          <td key={course.uuid} className="px-2 py-2 border-r border-border last:border-r-0">
                            <div className="flex justify-center">
                              <CapacityCell
                                value={cell}
                                onChange={(v) => updateCell(house.uuid, course.uuid, v)}
                                disabled={isInactive}
                                changed={changed}
                                maxCapacity={house.max_capacity}
                              />
                            </div>
                          </td>
                        );
                      })}

                      {/* Row actions */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 text-blue-600 hover:text-blue-800"
                            onClick={() => { setEditingHouse(house); setModalOpen(true); }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 p-0 text-red-600 hover:text-red-800"
                            onClick={() => setDeleteTarget(house)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Unsaved changes footer bar */}
      {isDirty && (
        <div className="fixed bottom-0 left-0 right-0 bg-card border-t shadow-lg z-50">
          <div className="container mx-auto py-3 px-6">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-muted-foreground">
                • {t('houseSettings.pendingChanges', { count: pendingChangeCount })}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  onClick={handleDiscardChanges}
                  disabled={isSavingMatrix}
                >
                  {t('houseSettings.discardChanges')}
                </Button>
                <Button onClick={handleSaveMatrix} disabled={isSavingMatrix}>
                  {isSavingMatrix
                    ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    : <Save className="mr-2 h-4 w-4" />}
                  {t('houseSettings.saveChanges')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add / Edit modal */}
      <HouseModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setEditingHouse(null); }}
        onSave={handleSaveHouse}
        initial={editingHouse}
        isSaving={isCreating || isUpdating}
      />

      {/* Update all course capacities prompt */}
      <Dialog open={!!capacityUpdatePrompt} onOpenChange={(o) => !o && setCapacityUpdatePrompt(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('houseSettings.updateCoursesTitle')}</DialogTitle>
            <DialogDescription>
              {t('houseSettings.updateCoursesDesc', {
                old: capacityUpdatePrompt?.oldCapacity,
                new: capacityUpdatePrompt?.newCapacity,
                name: capacityUpdatePrompt?.houseName,
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCapacityUpdatePrompt(null)}>
              {t('houseSettings.updateCoursesSkip')}
            </Button>
            <Button onClick={handleUpdateAllCourseCapacities}>
              {t('houseSettings.updateCoursesConfirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('houseSettings.deleteTitle')}</DialogTitle>
            <DialogDescription>
              {t('houseSettings.deleteConfirm', { name: deleteTarget?.name ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default HouseSettings;
